import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, TrendingUp, Users, DollarSign, AlertTriangle,
  Crown, UserCheck, UserX, Calendar, RefreshCw, Sparkles, Building2,
  MailCheck, MailWarning, Send, Heart,
} from 'lucide-react';

const INDIGO = '#4F46E5';
const INK = '#1C1917';
const MUTED = '#6B7280';

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days}d ago`;
    return fmtDate(iso);
  } catch { return iso; }
};

/* Tiny CSS-only bar chart for MRR trend — no extra dep */
function MrrTrendChart({ months }) {
  const max = Math.max(1, ...months.map((m) => m.mrr));
  return (
    <div className="grid gap-1.5" data-testid="mrr-trend-chart">
      <div className="flex items-end gap-2 h-40 sm:h-44 px-1">
        {months.map((m) => {
          const pct = (m.mrr / max) * 100;
          return (
            <div
              key={m.month}
              className="flex-1 flex flex-col items-center justify-end gap-1 group h-full"
              data-testid={`mrr-bar-${m.month}`}
            >
              <span className="text-[10px] font-semibold text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity">
                {fmtUSD(m.mrr)}
              </span>
              <div
                className="w-full rounded-t-md transition-all"
                style={{
                  height: `${Math.max(4, pct)}%`,
                  minHeight: '4px',
                  background: `linear-gradient(180deg, ${INDIGO} 0%, #818CF8 100%)`,
                }}
                title={`${m.label} · ${fmtUSD(m.mrr)} (${m.active} subs)`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 px-1">
        {months.map((m) => (
          <span
            key={m.month}
            className="flex-1 text-center text-[10px] font-medium text-gray-500"
          >
            {m.label.split(' ')[0]}
          </span>
        ))}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sublabel, accent = INDIGO, testId }) {
  return (
    <Card
      className="p-5 rounded-2xl border border-white/60 bg-white/90 backdrop-blur-xl shadow-sm hover:shadow-md transition-shadow"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
          <p
            className="text-2xl sm:text-3xl font-bold mt-1.5 tabular-nums"
            style={{ color: INK, fontFamily: 'Manrope, sans-serif' }}
            data-testid={`${testId}-value`}
          >
            {value}
          </p>
          {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
        </div>
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${accent}1A` }}
        >
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </Card>
  );
}

export default function RevenueDashboard({ token }) {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const authHeader = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const me = await axios.get(`${API}/admin/me`, authHeader);
        setIsAdmin(!!me.data?.is_admin);
        if (me.data?.is_admin) {
          await loadRevenue();
        }
      } catch {
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const loadRevenue = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/revenue`, authHeader);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load revenue data');
    } finally {
      setRefreshing(false);
    }
  };

  const [resendingFor, setResendingFor] = useState(null);
  const handleResendDunning = async (customerId, email) => {
    if (!customerId) {
      toast.error('No Stripe customer mapped — cannot resend.');
      return;
    }
    setResendingFor(customerId);
    try {
      const r = await axios.post(
        `${API}/admin/revenue/resend-dunning`,
        { customer_id: customerId },
        authHeader,
      );
      if (r.data?.email_sent) {
        toast.success(`Recovery email resent to ${r.data.email}`);
      } else {
        toast.warning(
          `Logged for ${r.data.email}, but email did NOT deliver — check Resend config.`
        );
      }
      // Refresh so last-sent timestamp updates
      loadRevenue({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingFor(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: INDIGO }} />
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-8 max-w-md text-center rounded-2xl">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Admin access only</h2>
          <p className="text-sm text-gray-600 mb-5">
            The Revenue Dashboard is restricted to platform owners.
          </p>
          <Button onClick={() => navigate('/')} className="rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const t = data?.totals || {};
  const recovery = data?.recovery || {};
  const trend = data?.mrr_trend || [];
  const cancellations = data?.recent_cancellations || [];
  const atRisk = data?.at_risk || [];
  const activations = data?.recent_activations || [];

  // MRR delta vs previous month (rough churn-vs-growth signal)
  const lastTwo = trend.slice(-2);
  const delta = lastTwo.length === 2 ? lastTwo[1].mrr - lastTwo[0].mrr : 0;
  const deltaPct = lastTwo.length === 2 && lastTwo[0].mrr > 0
    ? ((delta / lastTwo[0].mrr) * 100).toFixed(0)
    : null;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #FAF5FF 50%, #F0FDFA 100%)' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-white/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            data-testid="back-to-admin"
            className="rounded-xl shrink-0 px-3 sm:px-4"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Admin</span>
          </Button>
          <h1
            className="text-base sm:text-lg font-bold truncate"
            style={{ color: INK, fontFamily: 'Manrope, sans-serif' }}
          >
            Revenue Dashboard
          </h1>
          <Button
            onClick={() => loadRevenue()}
            disabled={refreshing}
            data-testid="refresh-revenue"
            variant="outline"
            className="rounded-xl shrink-0 px-3 sm:px-4"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline ml-2 sm:ml-0">Refresh</span>
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* As-of timestamp */}
        {data?.as_of && (
          <p className="text-xs text-gray-500 mb-5" data-testid="revenue-as-of">
            <Calendar className="w-3 h-3 inline mr-1" />
            Snapshot at {new Date(data.as_of).toLocaleString()}
          </p>
        )}

        {/* Top metric strip — MRR / ARR / Active subs / At-risk */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <MetricCard
            icon={DollarSign}
            label="MRR"
            value={fmtUSD(t.mrr_usd)}
            sublabel={
              deltaPct !== null
                ? `${delta >= 0 ? '↑' : '↓'} ${Math.abs(deltaPct)}% vs last month`
                : 'Monthly recurring revenue'
            }
            accent="#10B981"
            testId="metric-mrr"
          />
          <MetricCard
            icon={TrendingUp}
            label="ARR"
            value={fmtUSD(t.arr_usd)}
            sublabel="Annualized run rate"
            accent={INDIGO}
            testId="metric-arr"
          />
          <MetricCard
            icon={UserCheck}
            label="Active subs"
            value={(t.active_subscribers ?? 0).toLocaleString()}
            sublabel={t.canceling ? `${t.canceling} canceling at period end` : 'All renewing'}
            accent="#7C3AED"
            testId="metric-active"
          />
          <MetricCard
            icon={AlertTriangle}
            label="At risk"
            value={(t.past_due ?? 0).toLocaleString()}
            sublabel={t.past_due ? 'Past-due payments — review below' : 'No payment failures'}
            accent={t.past_due ? '#DC2626' : '#9CA3AF'}
            testId="metric-at-risk"
          />
        </div>

        {/* Funnel: Trials → Expired → Enterprise */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <MetricCard
            icon={Sparkles}
            label="Active trials"
            value={(t.trial_users ?? 0).toLocaleString()}
            sublabel="Within 7-day trial window"
            accent="#F59E0B"
            testId="metric-trials"
          />
          <MetricCard
            icon={UserX}
            label="Expired (no upgrade)"
            value={(t.expired_trial_users ?? 0).toLocaleString()}
            sublabel="Could be retargeted"
            accent="#EF4444"
            testId="metric-expired"
          />
          <MetricCard
            icon={Building2}
            label="Enterprise seats"
            value={(t.enterprise_users ?? 0).toLocaleString()}
            sublabel="Owners + invited clinicians"
            accent="#0EA5E9"
            testId="metric-enterprise"
          />
        </div>

        {/* MRR trend chart */}
        <Card
          className="p-5 sm:p-6 mb-6 sm:mb-8 rounded-2xl border border-white/60 bg-white/90 backdrop-blur-xl shadow-sm"
          data-testid="mrr-trend-card"
        >
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div>
              <h3 className="text-base sm:text-lg font-bold" style={{ color: INK, fontFamily: 'Manrope, sans-serif' }}>
                MRR — last 6 months
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Active monthly subscribers × $79.99 at end of each month
              </p>
            </div>
            {deltaPct !== null && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                  delta >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                }`}
                data-testid="mrr-delta-badge"
              >
                <TrendingUp className={`w-3 h-3 ${delta < 0 ? 'rotate-180' : ''}`} />
                {delta >= 0 ? '+' : ''}{fmtUSD(delta)} m/m
              </span>
            )}
          </div>
          {trend.length > 0 ? (
            <MrrTrendChart months={trend} />
          ) : (
            <p className="text-sm text-gray-500 text-center py-10">No data yet — your first paid sub will show up here.</p>
          )}
        </Card>

        {/* Recovery surface — dunning email tracking */}
        <Card
          className="p-5 sm:p-6 mb-6 sm:mb-8 rounded-2xl border border-white/60 bg-gradient-to-br from-emerald-50/70 via-white/90 to-rose-50/40 backdrop-blur-xl shadow-sm"
          data-testid="recovery-card"
        >
          <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Heart className="w-5 h-5 text-emerald-600" />
                <h3 className="text-base sm:text-lg font-bold" style={{ color: INK, fontFamily: 'Manrope, sans-serif' }}>
                  Payment recovery
                </h3>
              </div>
              <p className="text-xs text-gray-500">
                Dunning emails sent → updates landed → still at-risk (last 30 days)
              </p>
            </div>
            <span
              className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full ${
                (recovery.recovery_rate_pct ?? 0) >= 50
                  ? 'bg-emerald-100 text-emerald-800'
                  : (recovery.recovery_rate_pct ?? 0) >= 25
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-gray-100 text-gray-700'
              }`}
              data-testid="recovery-rate-badge"
            >
              {recovery.recovery_rate_pct ?? 0}% recovered
            </span>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <div
              className="p-4 rounded-xl bg-white/80 border border-white/60"
              data-testid="recovery-sent"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <MailCheck className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Emails sent</span>
              </div>
              <p
                className="text-2xl sm:text-3xl font-bold tabular-nums"
                style={{ color: INK, fontFamily: 'Manrope, sans-serif' }}
                data-testid="recovery-sent-value"
              >
                {recovery.dunning_sent_30d ?? 0}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {recovery.customers_emailed_30d ?? 0} unique customer{recovery.customers_emailed_30d === 1 ? '' : 's'}
              </p>
            </div>
            <div
              className="p-4 rounded-xl bg-emerald-50 border border-emerald-100"
              data-testid="recovery-recovered"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <UserCheck className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Recovered</span>
              </div>
              <p
                className="text-2xl sm:text-3xl font-bold tabular-nums text-emerald-800"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="recovery-recovered-value"
              >
                {recovery.recovered_30d ?? 0}
              </p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Card updated, charges succeeding
              </p>
            </div>
            <div
              className="p-4 rounded-xl bg-rose-50 border border-rose-100"
              data-testid="recovery-pending"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <MailWarning className="w-4 h-4 text-rose-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-rose-700">Pending</span>
              </div>
              <p
                className="text-2xl sm:text-3xl font-bold tabular-nums text-rose-800"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="recovery-pending-value"
              >
                {recovery.pending ?? 0}
              </p>
              <p className="text-xs text-rose-700 mt-0.5">
                Still past-due — review below
              </p>
            </div>
          </div>
          {(recovery.dunning_sent_total ?? 0) > 0 && (
            <p className="text-xs text-gray-400 mt-3" data-testid="recovery-lifetime-total">
              Lifetime: {recovery.dunning_sent_total} dunning email{recovery.dunning_sent_total === 1 ? '' : 's'} sent
            </p>
          )}
        </Card>

        {/* Two-column tables */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-6">
          {/* At-risk past-due */}
          <Card
            className="p-5 sm:p-6 rounded-2xl border border-white/60 bg-white/90 backdrop-blur-xl shadow-sm"
            data-testid="at-risk-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              <h3 className="text-base sm:text-lg font-bold" style={{ color: INK, fontFamily: 'Manrope, sans-serif' }}>
                At-risk accounts
              </h3>
              {atRisk.length > 0 && (
                <span className="ml-auto text-xs font-semibold bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full">
                  {atRisk.length}
                </span>
              )}
            </div>
            {atRisk.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">
                All payments succeeding ✓
              </p>
            ) : (
              <div className="space-y-2" data-testid="at-risk-list">
                {atRisk.map((s, i) => {
                  const isResending = resendingFor === s.stripe_customer_id;
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3 p-3 rounded-xl bg-rose-50/60 border border-rose-100"
                      data-testid={`at-risk-row-${i}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{s.email}</p>
                        <div className="flex items-center gap-2 flex-wrap text-xs text-rose-700 mt-0.5">
                          <span>Past due</span>
                          {s.card_brand && s.card_last4 ? (
                            <span>· {s.card_brand} ····{s.card_last4}</span>
                          ) : null}
                          {s.dunning_count > 0 && (
                            <span
                              className="inline-flex items-center gap-1 bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded"
                              data-testid={`at-risk-dunning-count-${i}`}
                              title={`Last sent: ${s.last_dunning_at ? fmtRel(s.last_dunning_at) : 'never'}`}
                            >
                              <MailCheck className="w-3 h-3" />
                              {s.dunning_count}× sent
                              {s.last_dunning_at ? ` · ${fmtRel(s.last_dunning_at)}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleResendDunning(s.stripe_customer_id, s.email)}
                        disabled={isResending || !s.stripe_customer_id}
                        size="sm"
                        variant="outline"
                        className="rounded-lg border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800 font-semibold shrink-0 h-8 px-3"
                        data-testid={`resend-dunning-btn-${i}`}
                      >
                        {isResending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <><Send className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Resend</span></>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Recent cancellations */}
          <Card
            className="p-5 sm:p-6 rounded-2xl border border-white/60 bg-white/90 backdrop-blur-xl shadow-sm"
            data-testid="cancellations-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <UserX className="w-5 h-5 text-amber-600" />
              <h3 className="text-base sm:text-lg font-bold" style={{ color: INK, fontFamily: 'Manrope, sans-serif' }}>
                Recent cancellations
              </h3>
              {cancellations.length > 0 && (
                <span className="ml-auto text-xs font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
                  {cancellations.length}
                </span>
              )}
            </div>
            {cancellations.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">
                No cancellations in the last 30 days 🎉
              </p>
            ) : (
              <div className="space-y-2" data-testid="cancellations-list">
                {cancellations.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-amber-50/60 border border-amber-100"
                    data-testid={`cancellation-row-${i}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{s.email}</p>
                      <p className="text-xs text-amber-700">
                        {s.cancel_at_period_end ? 'Canceling at period end' : 'Canceled'} · access until {fmtDate(s.current_period_end)}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 shrink-0">
                      {fmtRel(s.canceled_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Recent activations */}
        <Card
          className="p-5 sm:p-6 mt-5 sm:mt-6 rounded-2xl border border-white/60 bg-white/90 backdrop-blur-xl shadow-sm"
          data-testid="activations-card"
        >
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-5 h-5 text-emerald-600" />
            <h3 className="text-base sm:text-lg font-bold" style={{ color: INK, fontFamily: 'Manrope, sans-serif' }}>
              New paid customers (last 14 days)
            </h3>
            {activations.length > 0 && (
              <span className="ml-auto text-xs font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                {activations.length}
              </span>
            )}
          </div>
          {activations.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No new sign-ups yet — share the referral card on the dashboard to seed the funnel.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="activations-list">
              {activations.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-emerald-50/60 border border-emerald-100"
                  data-testid={`activation-row-${i}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{s.email}</p>
                    <p className="text-xs text-emerald-700">
                      {s.card_brand && s.card_last4 ? `${s.card_brand} ····${s.card_last4}` : 'Active'}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500 shrink-0">
                    {fmtRel(s.activated_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <p className="text-xs text-gray-400 text-center mt-8" style={{ color: MUTED }}>
          <Users className="w-3 h-3 inline mr-1" />
          Numbers are mirrored from Stripe via webhooks — refresh after a known billing event for the latest snapshot.
        </p>
      </main>
    </div>
  );
}
